import { assign, createMachine } from "xstate";
import { setAgreement } from "@nju/data-access-portal-user";
import { isError } from "@nju/result";
import { generalErrorMessage } from "@nju/utils";

interface IContext extends IAgreementState {
  errorMessage: string | undefined;
}

interface IAgreementState {
  currentAgreement: string;
  previousAgreement: string;
  allowSmsContact: boolean;
}

type IEvent =
  | { type: "TOGGLE_AGREEMENT" }
  | { type: "CONFIRM_SET_DISAGREEMENT" }
  | { type: "SET_DISAGREEMENT_SUCCESS"; newAgreementState: IAgreementState }
  | { type: "SET_DISAGREEMENT_ERROR" }
  | { type: "SET_AGREEMENT_SUCCESS"; newAgreementState: IAgreementState }
  | { type: "SET_AGREEMENT_ERROR" };

export const userProfileMachine = createMachine(
  {
    initial: "init",
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    states: {
      init: {
        on: {
          TOGGLE_AGREEMENT: [
            {
              cond: "allowSmsContact",
              target: "confirmAgreement",
            },
            {
              target: "disagreement",
            },
          ],
        },
      },
      disagreement: {
        on: {
          CONFIRM_SET_DISAGREEMENT: {
            target: "confirmDisagreement",
          },
        },
      },
      confirmDisagreement: {
        invoke: {
          src: "setAgreement",
        },
        on: {
          SET_DISAGREEMENT_SUCCESS: {
            actions: "toggleAgreement",
            target: "disagreementSuccess",
          },
          SET_DISAGREEMENT_ERROR: {
            target: "disagreementError",
          },
        },
      },
      disagreementSuccess: {
        type: "final",
      },
      disagreementError: {
        type: "final",
      },
      confirmAgreement: {
        invoke: {
          src: "setAgreement",
        },
        on: {
          SET_AGREEMENT_SUCCESS: {
            actions: "toggleAgreement",
            target: "init",
          },
          SET_AGREEMENT_ERROR: {
            actions: "setErrorMessage",
            target: "init",
          },
        },
      },
    },
  },
  {
    actions: {
      setErrorMessage: assign<IContext, IEvent>(() => {
        return {
          errorMessage: generalErrorMessage,
        };
      }),
      toggleAgreement: assign<IContext, IEvent>((context, event) => {
        if (
          event.type === "SET_DISAGREEMENT_SUCCESS" ||
          event.type === "SET_AGREEMENT_SUCCESS"
        ) {
          const { allowSmsContact, currentAgreement, previousAgreement } =
            event.newAgreementState;

          return {
            errorMessage: undefined,
            allowSmsContact,
            previousAgreement,
            currentAgreement,
          };
        }

        return context;
      }),
    },
    services: {
      setAgreement: ({ currentAgreement, allowSmsContact }) => {
        return async (callback) => {
          const newAgreementState = {
            previousAgreement: currentAgreement,
            currentAgreement: currentAgreement.includes("sms")
              ? "email,push"
              : "sms,email,push",
            allowSmsContact: currentAgreement.includes("sms"),
          };

          const result = await setAgreement({
            oldAgreement: newAgreementState.previousAgreement,
            newAgreement: newAgreementState.currentAgreement,
          });

          if (allowSmsContact) {
            if (isError(result)) {
              return callback({ type: "SET_AGREEMENT_ERROR" });
            }

            return callback({
              type: "SET_AGREEMENT_SUCCESS",
              newAgreementState,
            });
          }

          if (!allowSmsContact) {
            if (isError(result)) {
              return callback({ type: "SET_DISAGREEMENT_ERROR" });
            }

            return callback({
              type: "SET_DISAGREEMENT_SUCCESS",
              newAgreementState,
            });
          }
        };
      },
    },
    guards: {
      allowSmsContact: ({ allowSmsContact }) => {
        return allowSmsContact;
      },
    },
  },
);
